import { DownloadOutlined } from "@ant-design/icons"
import { Button, Skeleton, Tooltip } from "antd"
import { useContext } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useTheme } from "styled-components"
import { ContainerFull, LabeledBox, NameValueRow, OrderComments, ViewSampleSequence } from "../../components"
import { ORDER_STATUS_COLORS, ShowMessagePage } from "../../config"
import { UserContext } from "../../context"
import { isAdmin } from "../../utils/userUtils"
import { addOrderComment, getOrders } from "../../endpoints"

import { OrderCommentType } from "../../types"

export function UserOrderDetail(): JSX.Element {
    const navigate = useNavigate()
    const user = useContext(UserContext)
    const theme = useTheme()
    const { orderId } = useParams()
    const queryClient = useQueryClient()

    // console.log("orderId is ", orderId)
    // const [orderDetails, setOrderDetails] = useState<OrderInfoInterface>()
    // const [shippingDetails,setShippingDetails]=useState<string>('')
    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getorders", "one", orderId],
        queryFn: () => getOrders({ type: "one", orderIds: [parseInt(orderId ?? "0")] }),
        refetchOnMount: "always",
    })

    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error?.message ?? "unknown error")
    }

    if (data.code !== 1000) {
        return ShowMessagePage("Error", data.message)
    }
    const orders = data.orders
    let orderDetails = orders?.[0]
    let shippingDetails: string = ""
    if (orders && orders.length > 0) {
        const odetails = orders[0]
        let sdetails = "Method: " + odetails?.shippinginfo?.method
        if (odetails?.shipped) {
            if (odetails?.shippinginfo?.method === "dropbox") {
                sdetails += ": " + odetails.shippinginfo.dropbox?.label + ", samples dropped off"
            } else {
                sdetails += ", samples shipped"
            }
        } else {
            if (odetails?.shippinginfo?.method === "dropbox") {
                sdetails += ": " + odetails.shippinginfo.dropbox?.label + ", samples not dropped off"
            } else {
                sdetails += ", samples not shipped"
            }
        }
        if (odetails?.shippinginfo?.method === "pickup") {
            sdetails +=
                ", address: " +
                odetails?.shippinginfo.pickupinfo?.line1 +
                " " +
                odetails?.shippinginfo.pickupinfo?.line2 +
                " " +
                odetails?.shippinginfo.pickupinfo?.city
        }
        // setOrderDetails(odetails)
        // setShippingDetails(sdetails)
        shippingDetails = sdetails
    }

    // console.log(orderDetails)

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <Button onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
                &lt; Back
            </Button>

            <LabeledBox title={`Order ${orderDetails?.id}, Service: ${orderDetails?.productDetails?.name}`}>
                <NameValueRow key="name" Name={"Order name"} Value={orderDetails?.ordername} />
                {user?.user?.view_group_orders && (
                    <NameValueRow 
                        key="customer" 
                        Name={"Customer"} 
                        Value={`${orderDetails?.user?.firstName || ''} ${orderDetails?.user?.lastName || ''}`.trim()} 
                    />
                )}
                <NameValueRow
                    key="status"
                    Name={"Status"}
                    Value={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span
                                style={{
                                    backgroundColor: ORDER_STATUS_COLORS[orderDetails?.status ?? ""],
                                    // color: "#000",
                                }}
                            >
                                {orderDetails?.status ?? ""}
                            </span>
                            {(orderDetails?.status === "completed" || 
                              orderDetails?.status === "rerun-completed" ||
                              orderDetails?.status === "review-requested" ||
                              orderDetails?.status === "review-completed") && (
                                <Tooltip title="Download results">
                                    <a 
                                        href={`/api/download/order/${orderDetails?.id}`}
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            textDecoration: 'none'
                                        }}
                                    >
                                        <DownloadOutlined 
                                            style={{ 
                                                fontSize: 18, 
                                                color: '#1890ff',
                                                cursor: 'pointer'
                                            }} 
                                        />
                                    </a>
                                </Tooltip>
                            )}
                        </div>
                    }
                />

                <NameValueRow
                    key="concentration(ng/ul)"
                    Name={"Concentration (ng/ul)"}
                    Value={orderDetails?.concentration}
                />
                <NameValueRow
                    key="submissiontype"
                    Name={"Submission Type"}
                    Value={
                        orderDetails?.submission_type ? 
                        ({
                            'unknown': '',
                            'tubes': 'Tubes',
                            'plate-column-order': 'Plate (Column Order)',
                            'plate-row-order': 'Plate (Row Order)',
                            'plate-map': 'Plate (Custom Map)'
                        }[orderDetails.submission_type] || '') : ''
                    }
                />
                {orderDetails?.productid===6?(
                    // RCA QC
                    <NameValueRow 
                        key="itrPurityAnalysis" 
                        Name={"ITR purity analysis"} 
                        Value={orderDetails?.itrPurityAnalysis ? "Yes" : "No"} 
                    />
                ):orderDetails?.productid===7?(
                    // PolyA vector QC 
                    <NameValueRow 
                        key="linearizedPlasmid" 
                        Name={"Linearized Plasmid"} 
                        Value={orderDetails?.linearized ? "Yes" : "No"} 
                    />
                ):orderDetails?.productid===2?(
                    // Plasmid QC
                    <>
                    <NameValueRow 
                        key="colonysequencing" 
                        Name={"Colony Sequencing"} 
                        Value={orderDetails?.colonies ? "Yes" : "No"} 
                    />
                    <NameValueRow 
                        key="itrPurityAnalysis" 
                        Name={"ITR purity analysis"} 
                        Value={orderDetails?.itrPurityAnalysis ? "Yes" : "No"} 
                    /></>

                ):orderDetails?.productid===-1?(
                    // Amplicon Sequencing
                    <NameValueRow 
                        key="unpurifiedpcr" 
                        Name={"Unpurified PCR products"} 
                        Value={orderDetails?.unpurified ? "Yes" : "No"} 
                    />
                ):orderDetails?.productid===-6?(
                    // Plasmid Sequencing
                    <NameValueRow 
                        key="colonysequencing" 
                        Name={"Colony Sequencing"} 
                        Value={orderDetails?.colonies ? "Yes" : "No"} 
                    />
                ):null}
                
                <NameValueRow key="shipping" Name={"Shipping"} Value={shippingDetails} />

                <NameValueRow key="paymentmethod" Name={"Payment method"} Value={orderDetails?.paymentInfo?.method} />
                {orderDetails?.paymentInfo?.method === "PO" && (
                    <NameValueRow key="paymentinfo" Name={"PO number"} Value={orderDetails?.paymentInfo.PoNumber} />
                )}
                {orderDetails?.promotionCode && (
                    <NameValueRow key="promotioncode" Name={"Promotion code"} Value={orderDetails?.promotionCode} />
                )}
                <NameValueRow key="totalprice" Name={"Total price"} Value={"$"+orderDetails?.totalPrice?.price} />
                <NameValueRow 
                    key="paid" 
                    Name={"Paid"} 
                    Value={
                        <span style={{ 
                            color: orderDetails?.paymentInfo?.paid ? '#52c41a' : '#ff4d4f',
                            fontWeight: 500
                        }}>
                            {orderDetails?.paymentInfo?.paid ? 'Yes' : 'No'}
                        </span>
                    } 
                />
                <NameValueRow key="specialnotes" Name={"Special notes"} Value={orderDetails?.specialNotes} />
                <NameValueRow
                    key="date"
                    Name={"Order date"}
                    Value={new Date(orderDetails?.ts ?? "").toLocaleString()}
                />
                <NameValueRow
                    key="lastview"
                    Name={"Last viewed"}
                    Value={new Date(orderDetails?.lastViewedByCustomer ?? "").toLocaleString()}
                />
                <div
                    style={{
                        margin: "2em 0 2em 0",
                        padding: "1.25em 1.5em",
                        background: "#f6faff",
                        border: "1px solid #e6f4ff",
                        borderRadius: 10,
                        boxShadow: "0 2px 8px rgba(22,119,255,0.05)",
                    }}
                >
                    <div
                        style={{ fontWeight: 600, fontSize: 18, color: "#1677ff", marginBottom: 8, letterSpacing: 0.5 }}
                    >
                        Questions and feedback
                    </div>
                    <div style={{ color: "#666", marginBottom: 12, fontSize: 14 }}>
                        Leave a question or comment about your order. Our team will reply here.
                    </div>
                    <OrderComments
                        comments={orderDetails?.comments ?? []}
                        onAddComment={async ({ content, type }: { content: string; type: OrderCommentType }) => {
                            if (!orderDetails?.id) return
                            await addOrderComment({ orderId: orderDetails.id, content, type: type })
                            // Refetch order details to refresh comments
                            await queryClient.invalidateQueries({
                                queryKey: ["getorders", "one", orderId],
                            })
                        }}
                    />
                </div>

                {/* Plate Map Display */}
                {orderDetails?.samples && orderDetails.samples.length > 0 && 
                 (orderDetails.submission_type === 'plate-column-order' || orderDetails.submission_type === 'plate-row-order') && (() => {
                    const totalSamples = orderDetails.samples.length
                    const numPlates = Math.ceil(totalSamples / 96)
                    const plateOrder = orderDetails.submission_type === 'plate-row-order' ? 'row' : 'column'
                    
                    return (
                        <div style={{ marginTop: "1em", marginBottom: "1em" }}>
                            <div style={{ fontWeight: "bold", marginBottom: "8px", color: theme.primary }}>
                                Plate Layout ({totalSamples} samples, {numPlates} plate{numPlates > 1 ? 's' : ''}):
                            </div>
                            
                            {Array.from({ length: numPlates }).map((_, plateIndex) => {
                                const startSampleIndex = plateIndex * 96
                                const endSampleIndex = Math.min(startSampleIndex + 96, totalSamples)
                                const samplesInPlate = endSampleIndex - startSampleIndex
                                
                                return (
                                    <div key={`plate-${plateIndex}`} style={{ marginBottom: numPlates > 1 ? "24px" : "0" }}>
                                        {numPlates > 1 && (
                                            <div style={{ 
                                                fontWeight: "600", 
                                                marginBottom: "8px", 
                                                color: "#388087", 
                                                fontSize: "13px" 
                                            }}>
                                                Plate {plateIndex + 1} ({samplesInPlate} samples)
                                            </div>
                                        )}
                                        <div style={{ 
                                            display: "grid", 
                                            gridTemplateColumns: "24px repeat(12, minmax(45px, 1fr))", 
                                            gap: "2px",
                                            fontSize: "9px",
                                            backgroundColor: "#f5f5f5",
                                            padding: "6px",
                                            borderRadius: "4px",
                                            border: "1px solid #d9d9d9",
                                            maxWidth: "800px"
                                        }}>
                                            {/* Column headers */}
                                            <div style={{ fontWeight: "bold", textAlign: "center" }}></div>
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(col => (
                                                <div key={`col-${col}`} style={{ fontWeight: "bold", textAlign: "center", padding: "2px", fontSize: "9px" }}>
                                                    {col}
                                                </div>
                                            ))}
                                            
                                            {/* Plate wells */}
                                            {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((row, rowIndex) => (
                                                <>
                                                    {/* Row label */}
                                                    <div key={`row-${row}`} style={{ fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        {row}
                                                    </div>
                                                    
                                                    {/* Wells in this row */}
                                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((col, colIndex) => {
                                                        let sampleIndexInPlate = -1
                                                        
                                                        // Calculate sample index based on order type
                                                        if (plateOrder === 'row') {
                                                            // Row order: A1, A2, A3, ..., A12, B1, B2, ...
                                                            sampleIndexInPlate = rowIndex * 12 + colIndex
                                                        } else {
                                                            // Column order (default): A1, B1, C1, ..., H1, A2, B2, ...
                                                            sampleIndexInPlate = colIndex * 8 + rowIndex
                                                        }
                                                        
                                                        const globalSampleIndex = startSampleIndex + sampleIndexInPlate
                                                        const sample = orderDetails!.samples[globalSampleIndex]
                                                        const hasSample = sample !== undefined
                                                        
                                                        return (
                                                            <div
                                                                key={`${row}${col}`}
                                                                style={{
                                                                    minHeight: "38px",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    backgroundColor: hasSample ? "#388087" : "white",
                                                                    color: hasSample ? "white" : "#999",
                                                                    border: "1px solid #ccc",
                                                                    borderRadius: "2px",
                                                                    fontSize: "9px",
                                                                    lineHeight: "1.2",
                                                                    cursor: "default",
                                                                    fontWeight: hasSample ? "500" : "normal",
                                                                    overflow: "hidden",
                                                                    wordBreak: "break-word",
                                                                    padding: "3px",
                                                                    textAlign: "center"
                                                                }}
                                                                title={hasSample ? sample.sampleName : ''}
                                                            >
                                                                {hasSample ? sample.sampleName : ""}
                                                            </div>
                                                        )
                                                    })}
                                                </>
                                            ))}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )
                })()}

                <div
                    style={{
                        marginTop: "1em",
                        fontWeight: "bold",
                        color: theme.primary,
                    }}
                >
                    Samples:
                </div>
                {orderDetails?.samples?.map((s, index) => {
                    return <ViewSampleSequence key={index} sample={s} />
                })}
                {/* <Button
                    danger
                    disabled={orderDetails?.status !== "new"}
                    style={{ marginTop: "2em" }}
                    onClick={async () => {
                        Modal.confirm({
                            title: "Do you really want to cancel this order?",
                            icon: <ExclamationCircleFilled />,
                            cancelText: "No",
                            okText: "Yes, cancel it",
                            content: "You can only cancel orders that have not been processed.",
                            async onOk() {
                                const result = await updateOrderStatus({
                                    orderId: parseInt(orderId ?? "0"),
                                    newStatus: "canceled",
                                })
                                if (result.code === 1000) {
                                    navigate("/myspace/myorders", {
                                        state: {
                                            successMessage: "You have successfully canceled the order."
                                        }
                                    })
                                } else {
                                    Modal.error({ content: result.message })
                                }
                            },
                        })
                    }}
                >
                    Cancel order
                </Button> */}
            </LabeledBox>
            {isAdmin(user.user) && (
                <div
                    style={{
                        margin: "1em 0",
                        padding: "0.75em 1em",
                        background: "#fffbe6",
                        border: "1px solid #ffe58f",
                        borderRadius: 6,
                        color: "#ad8b00",
                        fontWeight: 500,
                    }}
                >
                    <b>Admin Notes:</b> Only new orders can be canceled by the user.
                </div>
            )}
        </ContainerFull>
    )
}
